import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheck,
  Newspaper,
  Scale,
  Building2,
  Landmark,
  AlertTriangle,
  Mail,
  Rocket,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import HussarTopNav from '../components/HussarTopNav';
import ProtocolBadge from '../components/ProtocolBadge';
import useRouteMeta from '../hooks/useRouteMeta';

/**
 * Public landing page for Tarcza Autentyczności (Authenticity Shield).
 *
 * STRICTLY a marketing/education surface. NO public analyzer, NO upload
 * form, NO API call, NO facial recognition, NO claims of certainty. The
 * actual tool lives at /admin/authenticity-shield (admin-only).
 */

const USE_CASES = [
  { key: 'newsrooms',     Icon: Newspaper },
  { key: 'lawFirms',      Icon: Scale },
  { key: 'organizations', Icon: Building2 },
  { key: 'administration',Icon: Landmark },
];

const TarczaAutentycznosci = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'pl').startsWith('en') ? 'en' : 'pl';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const pageUrl = `${origin}/tarcza-autentycznosci`;
  const imageUrl = `${origin}/og/tarcza-autentycznosci-og.png`;

  useRouteMeta({
    marker: 'tarcza-autentycznosci',
    title: t('authenticityPublic.metaTitle'),
    description: t('authenticityPublic.metaDescription'),
    canonical: pageUrl,
    locale: lang === 'en' ? 'en_GB' : 'pl_PL',
    ogImage: imageUrl,
    ogImageWidth: 1200,
    ogImageHeight: 630,
    ogImageAlt: t('authenticityPublic.heroH1'),
    ogUrl: pageUrl,
  });

  const analyzesItems = t('authenticityPublic.analyzes.items', { returnObjects: true }) || [];
  const notDoingItems = t('authenticityPublic.notDoing.items', { returnObjects: true }) || [];

  return (
    <div className="hussar-landing" data-testid="tarcza-autentycznosci-page">
      <HussarTopNav />

      {/* Hero */}
      <section
        className="hussar-section hussar-hero"
        data-testid="authpub-hero"
        style={{ paddingTop: 80 }}
      >
        <div className="hussar-container">
          <div
            data-testid="authpub-hero-eyebrow"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', borderRadius: 999,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(125, 211, 252, 0.06)',
              color: '#7DD3FC',
              fontSize: 11, letterSpacing: 1.2, fontWeight: 600,
              marginBottom: 18,
            }}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {t('authenticityPublic.heroEyebrow')}
          </div>
          <h1
            className="hussar-hero-title"
            data-testid="authpub-hero-h1"
            style={{ maxWidth: 920 }}
          >
            {t('authenticityPublic.heroH1')}
          </h1>
          <p
            className="hussar-hero-subtitle"
            data-testid="authpub-hero-subtitle"
            style={{ maxWidth: 800, marginTop: 16 }}
          >
            {t('authenticityPublic.heroSubtitle')}
          </p>
          <ProtocolBadge testidPrefix="authpub-hero" />
        </div>
      </section>

      {/* Doctrine card */}
      <section
        className="hussar-section"
        data-testid="authpub-doctrine-section"
        style={{ paddingTop: 16 }}
      >
        <div className="hussar-container">
          <Card
            data-testid="authpub-doctrine-card"
            style={{
              padding: 24,
              background: 'rgba(125, 211, 252, 0.04)',
              borderLeft: '3px solid #7DD3FC',
            }}
          >
            <h2
              data-testid="authpub-doctrine-title"
              style={{ fontSize: 20, fontWeight: 700, margin: 0 }}
            >
              {t('authenticityPublic.doctrine.title')}
            </h2>
            <p
              data-testid="authpub-doctrine-body"
              style={{ marginTop: 10, fontSize: 15, lineHeight: 1.55, opacity: 0.9 }}
            >
              {t('authenticityPublic.doctrine.body')}
            </p>
            <p
              data-testid="authpub-doctrine-line"
              style={{
                marginTop: 14,
                padding: 10,
                borderRadius: 6,
                background: 'rgba(245,158,11,0.06)',
                borderLeft: '3px solid #F59E0B',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {t('authenticityPublic.doctrine.line')}
            </p>
          </Card>
        </div>
      </section>

      {/* Use cases */}
      <section
        className="hussar-section"
        data-testid="authpub-use-cases-section"
        style={{ paddingTop: 24 }}
      >
        <div className="hussar-container">
          <h2 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}
              data-testid="authpub-use-cases-heading">
            {t('authenticityPublic.useCases.heading')}
          </h2>
          <div
            data-testid="authpub-use-cases-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 16, marginTop: 20,
            }}
          >
            {USE_CASES.map(({ key, Icon }) => (
              <Card
                key={key}
                data-testid={`authpub-use-case-${key}`}
                style={{
                  padding: 20,
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div
                  style={{
                    width: 36, height: 36, borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(125, 211, 252, 0.08)', color: '#7DD3FC',
                  }}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 600, margin: '12px 0 8px' }}>
                  {t(`authenticityPublic.useCases.${key}.title`)}
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.55, opacity: 0.85, margin: 0 }}>
                  {t(`authenticityPublic.useCases.${key}.body`)}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What it analyses + What it does NOT do — side by side */}
      <section
        className="hussar-section"
        data-testid="authpub-capabilities-section"
        style={{ paddingTop: 24 }}
      >
        <div className="hussar-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 16,
          }}>
            <Card
              data-testid="authpub-analyzes-card"
              style={{ padding: 22, background: 'rgba(34, 197, 94, 0.04)', borderLeft: '3px solid #34D399' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle2 className="w-4 h-4" style={{ color: '#34D399' }} />
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}
                    data-testid="authpub-analyzes-heading">
                  {t('authenticityPublic.analyzes.heading')}
                </h3>
              </div>
              <ul
                data-testid="authpub-analyzes-list"
                style={{ marginTop: 12, paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}
              >
                {analyzesItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Card>
            <Card
              data-testid="authpub-not-doing-card"
              style={{ padding: 22, background: 'rgba(239, 68, 68, 0.04)', borderLeft: '3px solid #F87171' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <XCircle className="w-4 h-4" style={{ color: '#F87171' }} />
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}
                    data-testid="authpub-not-doing-heading">
                  {t('authenticityPublic.notDoing.heading')}
                </h3>
              </div>
              <ul
                data-testid="authpub-not-doing-list"
                style={{ marginTop: 12, paddingLeft: 18, fontSize: 14, lineHeight: 1.7 }}
              >
                {notDoingItems.map((item, idx) => (
                  <li key={item} data-testid={`authpub-not-doing-item-${idx}`}>{item}</li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="hussar-section"
        data-testid="authpub-cta-section"
        style={{ paddingTop: 30, paddingBottom: 80 }}
      >
        <div className="hussar-container">
          <Card
            style={{
              padding: 28,
              background: 'rgba(125, 211, 252, 0.05)',
              borderLeft: '3px solid #7DD3FC',
            }}
          >
            <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}
                data-testid="authpub-cta-headline">
              {t('authenticityPublic.cta.headline')}
            </h2>
            <p style={{ marginTop: 10, fontSize: 15, opacity: 0.9, lineHeight: 1.55 }}
               data-testid="authpub-cta-body">
              {t('authenticityPublic.cta.body')}
            </p>
            <div style={{ marginTop: 18, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link
                to="/sposoby-wspolpracy"
                className="hussar-btn-primary"
                data-testid="authpub-cta-primary"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 22px', borderRadius: 8,
                  textDecoration: 'none', fontWeight: 600,
                }}
              >
                {t('authenticityPublic.cta.primary')} <Rocket className="w-4 h-4" />
              </Link>
              <a
                href="mailto:audyt@hussarai.org?subject=Tarcza%20Autentyczno%C5%9Bci%20-%20pilota%C5%BC"
                className="hussar-btn-secondary"
                data-testid="authpub-cta-secondary"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 22px', borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.12)',
                  textDecoration: 'none', fontWeight: 600,
                  color: 'var(--hussar-text-primary, #E5E7EB)',
                }}
              >
                {t('authenticityPublic.cta.secondary')} <Mail className="w-4 h-4" />
              </a>
            </div>
          </Card>

          <p
            data-testid="authpub-footer-doctrine"
            style={{
              marginTop: 22, fontSize: 12, opacity: 0.55,
              textAlign: 'left', fontStyle: 'italic',
            }}
          >
            <AlertTriangle className="w-3 h-3 inline mr-1" style={{ verticalAlign: '-2px' }} />
            {t('authenticityPublic.doctrine.line')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default TarczaAutentycznosci;
